import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';
import {
    Box,
    Collapse,
    IconButton,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
} from '@mui/material';
import React, {useEffect, useRef, useState} from 'react';
import {CustomCheckbox} from '../CustomCheckbox';
import {useTranslation} from 'react-i18next';

import {
    StyledTable,
    StyledTableRow,
    StyledHeaderCell, CustomDropdownMenuStyles,
} from "../Styles";

import {getDateFormat} from "../../Apis/functions";
import EllipsisCell from "./EllipsisCell";
import CustomTooltip from "../CustomTooltip";

// Utility: Access nested object fields like 'user.profile.name'
const getValueByPath = (obj, path) => {
    return path.split('.').reduce((acc, part) => acc?.[part], obj);
};

const CustomExpandableTable = ({
                                   id = "tbl_cet_accordion",
                                   columns, // Array: Defines the table columns. Each object should contain `field`, `label`, and optionally `render` for custom rendering.
                                   rows, // Array: The data to render in the table rows.
                                   renderExpandedRow, // Function (Optional): A function that returns the content to show when a row is expanded.
                                   renderOptionsMenu, // Function (Optional): A function that returns action elements (e.g., menu or buttons) for each row.
                                   onRowExpand, // Function (Optional): A callback triggered when a row is expanded.
                                   handleCheckboxChange, // Function: Called when an individual row checkbox is toggled.
                                   selectedList = [], // Array: IDs of currently selected rows.
                                   rowsPerPage, // Number: Determines how many rows should be displayed per page.
                                   handleDeleteAll,  // Function: Callback executed when the "delete all selected rows" action is triggered.
                                   checkbox = true, // Boolean: Enables or disables the checkbox column. If false, no checkboxes are shown.
                                   deSelectAllId = 'cb_cet_de_select_all',// Id for the "deselect all" checkbox.
                                   tooltip = true,// Boolean: Determines whether tooltips are shown on elements within the table.
                                   placement = 'top-start', // String: Tooltip placement position (e.g., 'top', 'bottom', 'left', 'right').
                                   className='',
                                   showHeader = true,   // Boolean: Controls whether the table header is displayed.
                                   rowClassName = null, // Optional callback that allows assigning dynamic CSS classes to table rows.
                                   classNameRenderContainer='',
                                   withoutHeight=false // Boolean: Disables the automatic height calculation and the dynamic positioning of the accordion popper.
                               }) => {

    const [openRowId, setOpenRowId] = useState(null);
    const {t} = useTranslation();
    const elementRef = useRef(null);
    const tbodyRef = useRef(null);


    const toggleRow = (rowId, row) => {
        const isExpanding = openRowId !== rowId;
        if (isExpanding && onRowExpand) {
            onRowExpand(row);
        }
        setOpenRowId(isExpanding ? rowId : null);
    };

    useEffect(() => {
        if(withoutHeight) return;

        if (elementRef.current) {
            const rect = elementRef.current.getBoundingClientRect();
            const distanceTop = rect.top + 2;
            elementRef.current.style.setProperty('--top-accordion-value', `${distanceTop}px`);
        }
    }, [rows]);

    const totalCols = columns.filter(col => col.visible !== false).length;

    return (
        <TableContainer ref={elementRef} className={"panelAccordion-container custom-scrollbar"}>
            <StyledTable id={id} aria-label="custom expandable table" columnsCount={totalCols} checkbox={checkbox} openRowId={openRowId}>
                {showHeader && (
                <TableHead>
                    <TableRow>
                        {checkbox &&
                        <StyledHeaderCell padding="checkbox" columnsCount={totalCols} checkbox={checkbox} >
                            <CustomCheckbox
                                id={deSelectAllId}
                                checked={rows?.slice(0, rowsPerPage).every((row) => selectedList.includes(row?.id))}
                                indeterminate={
                                    selectedList.length > 0 &&
                                    !rows.slice(0, rowsPerPage).every((row) => selectedList.includes(row?.id))
                                }
                                onChange={(e) => {
                                    handleDeleteAll(e)
                                }}
                                size="small"
                                className={"checkbox-head"}
                            />
                        </StyledHeaderCell>
                        }
                        {columns.map((col) => (
                            <StyledHeaderCell isSelected={selectedList.includes(col.id)} columnsCount={totalCols}  key={col.field} checkbox={checkbox}
                                              className={col.className}>{t(`${col.label}`)}</StyledHeaderCell>
                        ))}
                        {renderOptionsMenu &&
                        <StyledHeaderCell align="center" className={"w-100px"}>{t('options')}</StyledHeaderCell>}
                        {renderExpandedRow &&
                        <StyledHeaderCell align="center" className={"w-100px"}>{t('see_more')}</StyledHeaderCell>}
                    </TableRow>
                </TableHead>
             )}
                <TableBody ref={tbodyRef}>
                    {rows?.map((row) => (
                        <React.Fragment key={row.id}>
                            <StyledTableRow checkbox={checkbox} isActive={openRowId}  className={` ${openRowId === row?.id ? 'rowActive' : ''}  ${rowClassName ? rowClassName(row) : ''} `}>
                                {checkbox &&
                                <CustomTooltip text={row?.disabledCheckboxText}>
                                    <TableCell padding="checkbox">
                                            <CustomCheckbox
                                                id={"cb_cet_row_" + row?.id}
                                                checked={selectedList.includes(row?.id)}
                                                onChange={(e) => {
                                                    if (!row || !row.id) return;
                                                    handleCheckboxChange({target: {id: row.id, checked: e.target.checked}});
                                                }}
                                                disabled={row?.disabled || false}
                                            />
                                    </TableCell>
                                </CustomTooltip>
                                }
                                {columns.map((col) => {
                                    let value = getValueByPath(row, col?.field);

                                    if (typeof value === 'number' && value > 1_000_000_000_000) {
                                        value = getDateFormat(
                                            value,
                                            typeof col?.datetime === "undefined" ? true : col?.datetime,
                                            col?.dateFormat
                                        );
                                    }
                                    return (
                                        <TableCell key={col.field} className={col.classTableCell}>
                                            <EllipsisCell
                                                value={value}
                                                render={(val) => col.render ? col.render(val, row) : val}
                                                tooltip={tooltip}
                                                placement={placement}
                                                className={col?.className}
                                                classNameContainerTooltip={col?.classNameContainerTooltip}
                                                classNameTooltip={col?.classNameTooltip}
                                                classNameSubTextTooltip={col?.classNameSubTextTooltip}
                                            />
                                        </TableCell>
                                    );
                                })}
                                {renderOptionsMenu &&
                                <TableCell align="center">{renderOptionsMenu(row)}</TableCell>
                                }
                                {renderExpandedRow && (
                                    <TableCell align="center">
                                        <IconButton disableRipple>
                                            {
                                                openRowId === row?.id ?
                                                    <ArrowDropUpIcon sx={CustomDropdownMenuStyles.iconButton}
                                                                     className={'rowActive'}
                                                                     onClick={() => toggleRow(row?.id, row)}
                                                                     size="small"/> :
                                                    <ArrowDropDownIcon sx={CustomDropdownMenuStyles.iconButton}
                                                                       onClick={() => toggleRow(row?.id, row)}
                                                                       size="small"/>
                                            }
                                        </IconButton>
                                    </TableCell>
                                )}
                            </StyledTableRow>
                            {renderExpandedRow && (
                                <TableRow className='transform-negative-8px'>
                                    <TableCell colSpan={columns.length + 3} className={'p-0 border-0'}>
                                        <Collapse in={openRowId === row?.id} timeout='auto' unmountOnExit>
                                            <Box
                                                className={`${classNameRenderContainer} p-16px background-shades-50 expanded-row-box`}
                                            >
                                                {React.createElement(renderExpandedRow, { row })}
                                            </Box>
                                        </Collapse>
                                    </TableCell>
                                </TableRow>
                            )}
                        </React.Fragment>
                    ))}
                </TableBody>
            </StyledTable>
        </TableContainer>
    );
};

export default CustomExpandableTable;
